import { getAccessToken } from './authToken';

const BACKEND = process.env.REACT_APP_BACKEND_WORKER_URL;

/**
 * Central HTTP client for all backend requests with automatic JWT injection and error handling.
 * @param {string} path - The API path relative to the backend (e.g. '/api/v1/crm/deals')
 * @param {Object} [options] - Optional fetch configuration
 * @param {string} [options.method='GET'] - The HTTP method (GET, POST, PATCH, PUT, DELETE)
 * @param {Object} [options.body] - The request body, automatically serialized as JSON
 * @param {Object} [options.headers] - Additional HTTP headers (merged with the auth header)
 * @param {'json'|'blob'|'stream'} [options.responseType='json'] - The desired response format
 * @returns {Promise<Object|Blob|Response|null>} The parsed response (JSON object, Blob, Response stream, or null for 204)
 * @throws {Error} Error with `status`, `code`, `details`, and `validationErrors` on non-successful responses
 */
export async function fetcher(path, { method = 'GET', body, headers, responseType = 'json', ...rest } = {}) {
	const token = await getAccessToken();

	const res = await fetch(`${BACKEND}${path}`, {
		method,
		headers: {
			'Content-Type': 'application/json',
			...(token && { Authorization: `Bearer ${token}` }),
			...headers,
		},
		body: body ? JSON.stringify(body) : undefined,
		...rest,
	});

	if (!res.ok) {
		let errorData;
		const contentType = res.headers.get('content-type');
		if (contentType?.includes('application/json')) {
			errorData = await res.json();
		} else {
			errorData = { message: await res.text() };
		}
		const error = new Error(errorData.errorMessage || `Request failed: ${res.status}`);
		error.status = res.status;
		error.code = errorData.errorCode ?? null;
		error.details = errorData;
		error.validationErrors = errorData.validationErrors ?? null;
		throw error;
	}

	// Handle 204 No Content (e.g., DELETE requests)
	if (res.status === 204) {
		return null;
	}

	if (responseType === 'blob') return res.blob();
	if (responseType === 'stream') return res;

	return res.json();
}
